import { fmtAmount } from "./dashboardKit";

const PARTS = [
  { key: "paid", label: "Paid", dot: "bg-success-500" },
  { key: "open", label: "Not yet due", dot: "bg-brand-400" },
  { key: "overdue", label: "Overdue", dot: "bg-danger-500" },
];

export const invoiceTotals = (invoices, now = new Date()) => {
  const byCurrency = new Map();
  invoices.forEach((inv) => {
    const code =
      inv.flatRateReceivable?.currency?.currency?.code ||
      inv.unitBasedReceivable?.currency?.currency?.code ||
      "";
    const row = byCurrency.get(code) ?? { code, count: 0, total: 0, paid: 0, open: 0, overdue: 0 };
    const due = Number(inv.due_amount || 0);
    row.count += 1;
    row.total += Number(inv.total || 0);
    row.paid += Number(inv.paid_amount || 0);
    if (due > 0 && inv.due_at && new Date(inv.due_at) < now) row.overdue += due;
    else row.open += due;
    byCurrency.set(code, row);
  });
  return [...byCurrency.values()].sort((a, b) => b.total - a.total);
};

const InvoiceSummary = ({ totals, loading }) => {
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-40 animate-pulse rounded-md bg-gray-100" />
        <div className="h-2.5 animate-pulse rounded-full bg-gray-100" />
        <div className="h-4 w-56 animate-pulse rounded bg-gray-50" />
      </div>
    );
  }

  if (!totals.length) return <p className="text-sm text-gray-400">No client invoices yet.</p>;

  return (
    <div className="space-y-5">
      {totals.map((row) => {
        const outstanding = row.open + row.overdue;
        const sum = row.paid + outstanding || 1;
        return (
          <div key={row.code || "none"}>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Outstanding</p>
                <p className="text-2xl font-semibold tracking-tight text-gray-900">
                  {fmtAmount(outstanding, { code: row.code })}
                </p>
              </div>
              <p className="text-right text-xs text-gray-500">
                {row.count} invoice{row.count === 1 ? "" : "s"}
                <br />
                {fmtAmount(row.total, { code: row.code })} billed
              </p>
            </div>

            <div className="mt-3 flex h-2.5 gap-[2px] overflow-hidden rounded-full bg-gray-100">
              {PARTS.map((part) =>
                row[part.key] > 0 ? (
                  <span
                    key={part.key}
                    title={`${part.label}: ${fmtAmount(row[part.key], { code: row.code })}`}
                    style={{ width: `${(row[part.key] / sum) * 100}%` }}
                    className={`h-full first:rounded-l-full last:rounded-r-full ${part.dot}`}
                  />
                ) : null
              )}
            </div>

            <ul className="mt-3 grid grid-cols-3 gap-2">
              {PARTS.map((part) => (
                <li key={part.key} className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${part.dot}`} />
                    {part.label}
                  </span>
                  <span className="mt-0.5 block truncate text-xs font-semibold tabular-nums text-gray-900">
                    {fmtAmount(row[part.key], { code: row.code })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default InvoiceSummary;